/* MolCellQuest 問題データ — 創薬・抗体医薬・細胞工学
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "applied", category: "medicine", categoryName: "創薬・抗体医薬・細胞工学" };

export const APPLIED_MEDICINE = [
  {
    id: "med01", ...CAT,
    title: "抗体の構造と抗原結合部位", diff: 105, tags: ["抗体", "IgG", "Fab", "Fc"],
    explain: `<p>IgG抗体の模式図を考える。抗原を直接認識している部分はどれか。</p>`,
    type: "radio",
    options: [
      "重鎖の定常領域（Fc領域）",
      "重鎖と軽鎖の可変領域が向かい合ってつくるくぼみ",
      "重鎖どうしをつなぐジスルフィド結合",
      "糖鎖が付いている領域",
    ],
    answer: 1,
    hints: [
      "抗体は Y 字型で、Y の先端2か所と足の部分で役割が違います。",
      "「可変（variable）」と呼ばれる理由を考えてみましょう。",
    ],
    solution: `<p><b>【ステップ1】IgG は 4 本鎖の Y 字</b></p>
               <p>重鎖（H鎖）2本と軽鎖（L鎖）2本がジスルフィド結合で組み立てられ、
               Y 字型をとります。Y を切り分けると：</p>
               <table>
                 <tr><th>断片</th><th>構成</th><th>役割</th></tr>
                 <tr><td><b>Fab</b>（2個）</td><td>H鎖の一部＋L鎖</td><td><b>抗原結合</b></td></tr>
                 <tr><td><b>Fc</b>（1個）</td><td>H鎖の定常領域</td><td>細胞・補体との相互作用</td></tr>
               </table>
               <p><b>【ステップ2】抗原を見ているのは可変領域</b></p>
               <p>Fab の先端では H鎖可変領域（V<sub>H</sub>）と L鎖可変領域（V<sub>L</sub>）が向かい合い、
               その中の<b>相補性決定領域（CDR、各鎖3か所＝計6か所）</b>が
               抗原の表面（エピトープ）にはまり込みます。
               この6つのループの配列が抗体ごとに違うので、
               ほぼ無限の抗原に対応できるわけです。</p>
               <p><b>【ステップ3】Fc は「効果」を決める</b></p>
               <p>Fc は抗原とは結合しませんが、
               マクロファージ・NK細胞の <b>Fc受容体</b>や<b>補体 C1q</b> と結合します。
               つまり <b>Fab が「誰を狙うか」、Fc が「何をするか」</b>を決めます。
               抗体医薬の設計がこの2つを別々にいじるのは、この分業があるからです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med02", ...CAT,
    title: "モノクローナル抗体とポリクローナル抗体", diff: 125, tags: ["モノクローナル抗体", "エピトープ", "B細胞"],
    explain: `<p>ウサギに抗原を注射して得た血清（抗血清）と、
              マウスのハイブリドーマから得た抗体を比べる。
              <b>モノクローナル抗体</b>の特徴として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "多数のエピトープを同時に認識するので、変性した抗原でも検出しやすい。",
      "単一のエピトープだけを認識し、同じ性質の抗体を半永久的に供給できる。",
      "動物ごとにロット差が大きいが、感度は必ず高い。",
      "抗原がなくても作れるので、抗原の精製が不要である。",
    ],
    answer: 1,
    hints: [
      "「クローン」とは、もとは1個の細胞から増えた集団という意味です。",
      "血清は多数のB細胞クローンが作った抗体の混合物です。",
    ],
    solution: `<p><b>【ステップ1】1つのB細胞は1種類の抗体しか作らない</b></p>
               <p>これが出発点です。抗原を注射すると、
               その抗原上の<b>いろいろなエピトープ</b>に反応する多数のB細胞クローンが増えます。
               血清（＝ポリクローナル抗体）は、その混合物です。</p>
               <p><b>【ステップ2】比較</b></p>
               <table>
                 <tr><th></th><th>ポリクローナル</th><th>モノクローナル</th></tr>
                 <tr><td>認識</td><td>多数のエピトープ</td><td><b>単一のエピトープ</b></td></tr>
                 <tr><td>供給</td><td>動物ごと・ロット差あり</td><td><b>細胞株から無限に</b></td></tr>
                 <tr><td>得意</td><td>少量の抗原の検出、変性抗原</td><td>特異性、再現性、医薬品化</td></tr>
                 <tr><td>弱点</td><td>交差反応、再現性</td><td>そのエピトープが壊れると全滅</td></tr>
               </table>
               <p><b>【ステップ3】なぜ医薬品はモノクローナルなのか</b></p>
               <p>医薬品には<b>常に同じ品質のものを作り続けられる</b>ことが要求されます。
               ハイブリドーマや組換え細胞株は凍結保存でき、
               何年後でも同一配列の抗体を供給できます。
               これが、ポリクローナル抗血清では原理的に満たせない条件です。</p>
               <p class="tiny">A は<b>ポリクローナル</b>の長所です（複数のエピトープを見るため、
               1か所が変性しても他で拾える）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med03", ...CAT,
    title: "ハイブリドーマ法の流れ", diff: 155, tags: ["ハイブリドーマ", "細胞融合", "ミエローマ", "モノクローナル抗体"],
    explain: `<p>ケーラーとミルスタインのハイブリドーマ法では、
              抗原を免疫したマウスの脾臓B細胞と、ミエローマ（骨髄腫）細胞を融合させる。</p>
              <p>この組み合わせを使う理由として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "B細胞は無限に増殖できるが抗体を作らず、ミエローマは抗体を作るが増殖できないから。",
      "B細胞は目的の抗体を作るが長く生きられず、ミエローマは無限に増殖できるから。",
      "どちらも抗体を作るので、2種類の抗体を同時に得られるから。",
      "融合によってB細胞の抗体遺伝子が変異し、親和性が上がるから。",
    ],
    answer: 1,
    hints: [
      "培養皿の中で、正常なB細胞は何日くらい生きるでしょうか。",
      "「不死化」という言葉がどちらの細胞に当てはまるか考えましょう。",
    ],
    solution: `<p><b>【ステップ1】2つの細胞が持ち寄るもの</b></p>
               <table>
                 <tr><th>細胞</th><th>持っている性質</th><th>足りない性質</th></tr>
                 <tr><td>免疫マウスのB細胞</td><td><b>目的抗原に対する抗体を作る</b></td><td>培養では数日で死ぬ</td></tr>
                 <tr><td>ミエローマ細胞</td><td><b>無限に増殖する（不死化）</b></td><td>目的の抗体を作らない</td></tr>
               </table>
               <p>融合細胞（ハイブリドーマ）は<b>「目的の抗体を作りつづける不死の細胞」</b>になります。</p>
               <p><b>【ステップ2】手順</b></p>
               <ol>
                 <li>マウスに抗原を免疫する</li>
                 <li>脾臓からB細胞を取り出し、ミエローマ細胞と<b>ポリエチレングリコール（PEG）</b>や
                     電気パルスで融合させる</li>
                 <li><b>HAT培地</b>で選択する（融合細胞だけが生き残る → med04）</li>
                 <li>1ウェル1細胞になるまで希釈して<b>クローン化（限界希釈法）</b></li>
                 <li>各ウェルの培養液を ELISA でスクリーニングし、目的抗体を作るクローンを選ぶ</li>
               </ol>
               <p><b>【ステップ3】クローン化を省けない理由</b></p>
               <p>融合直後の集団は多数のハイブリドーマの混合物です。
               「1個の細胞から増やす」段階を通さなければ<b>モノクローナル</b>にはなりません。
               ここが手法の名前の由来であり、実験の核心です。</p>
               <p class="tiny">1975年の報告。ケーラーとミルスタインは1984年のノーベル生理学・医学賞。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med04", ...CAT,
    title: "HAT選択はなぜ融合細胞だけを残せるか", diff: 265, tags: ["HAT培地", "HGPRT", "サルベージ経路", "選択"],
    explain: `<p>ハイブリドーマの選択には、<b>HAT培地</b>（ヒポキサンチン・アミノプテリン・チミジン）と、
              <b>HGPRT欠損</b>のミエローマ細胞を用いる。</p>
              <p>アミノプテリンは葉酸代謝を阻害し、ヌクレオチドの<b>新規（de novo）合成</b>を止める。
              このとき培養皿に生き残る細胞と、その理由の組み合わせで正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "融合細胞のみ。B細胞由来のHGPRTでサルベージ経路が使え、ミエローマ由来の不死化能で増殖できる。",
      "ミエローマのみ。HGPRTがなくても de novo 合成が働くため。",
      "B細胞のみ。HGPRTを持ちサルベージ経路が使えるため。",
      "すべての細胞。ヒポキサンチンとチミジンが加わっているため誰も困らない。",
    ],
    answer: 0,
    hints: [
      "de novo 合成が止まったとき、細胞に残る道はサルベージ（再利用）経路だけです。",
      "サルベージ経路に必要な酵素 HGPRT を、B細胞とミエローマのどちらが持っているでしょうか。",
    ],
    solution: `<p><b>【ステップ1】アミノプテリンで de novo 経路を封鎖する</b></p>
               <p>アミノプテリン（メトトレキサートの類縁体）はジヒドロ葉酸還元酵素を阻害し、
               プリン・チミジンの<b>新規合成を止めます</b>。
               すると細胞は、外から前駆体を取り込む<b>サルベージ経路</b>に頼るしかありません。</p>
               <p><b>【ステップ2】サルベージ経路に必要な酵素</b></p>
               <ul>
                 <li>ヒポキサンチン → プリン：<b>HGPRT</b> が必要</li>
                 <li>チミジン → dTMP：<b>チミジンキナーゼ（TK）</b> が必要</li>
               </ul>
               <p>培地に加えたヒポキサンチン（H）とチミジン（T）は、この材料です。
               つまり HAT 培地は「新規合成を止めたうえで、再利用の材料だけ与える」設計です。</p>
               <p><b>【ステップ3】3種類の細胞の運命</b></p>
               <table>
                 <tr><th>細胞</th><th>HGPRT</th><th>増殖能</th><th>結果</th></tr>
                 <tr><td>未融合ミエローマ</td><td>×（欠損株を使う）</td><td>○</td><td><b>死ぬ</b>（ヌクレオチドを作れない）</td></tr>
                 <tr><td>未融合B細胞</td><td>○</td><td>×</td><td><b>数日で寿命が尽きる</b></td></tr>
                 <tr><td>ハイブリドーマ</td><td>○（B細胞由来）</td><td>○（ミエローマ由来）</td><td><b>生き残って増える</b></td></tr>
               </table>
               <p>2つの欠点を互いに補い合った細胞だけが条件をくぐり抜ける、という
               <b>二重選択</b>の論理です。</p>
               <p class="tiny">HGPRT欠損株が 6-チオグアニン耐性として単離できるのも同じ原理です
               （HGPRTがあると毒性のある塩基アナログを取り込んでしまう）。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "med05", ...CAT,
    title: "マウス抗体をヒト化する理由", diff: 185, tags: ["ヒト化抗体", "キメラ抗体", "HAMA", "CDR"],
    explain: `<p>マウス由来のモノクローナル抗体をそのままヒトに投与すると、
              しばしば効果が続かない。改良の歴史は
              <b>マウス抗体 → キメラ抗体 → ヒト化抗体 → 完全ヒト抗体</b>と進んだ。</p>
              <p>この流れの主な目的はどれか。</p>`,
    type: "radio",
    options: [
      "抗原への親和性を上げ、投与量を減らすため。",
      "ヒトの免疫系がマウス抗体を異物として攻撃するのを避け、中和や急速な排除を防ぐため。",
      "抗体を小さくして経口投与できるようにするため。",
      "抗体が細胞内に入れるようにするため。",
    ],
    answer: 1,
    hints: [
      "マウスのタンパク質をヒトに入れると、ヒトの体は何をするでしょうか。",
      "HAMA（ヒト抗マウス抗体）という言葉があります。",
    ],
    solution: `<p><b>【ステップ1】マウス抗体そのものが抗原になる</b></p>
               <p>ヒトにとってマウスIgGは異物です。投与するとヒト側が
               <b>HAMA（human anti-mouse antibody）</b>を作り、</p>
               <ul>
                 <li>抗体を中和して効果を失わせる</li>
                 <li>免疫複合体として急速に排除する（半減期の短縮）</li>
                 <li>アレルギー反応を起こす</li>
               </ul>
               <p>という問題が生じます。反復投与が必要な医薬品にとっては致命的です。</p>
               <p><b>【ステップ2】どこをヒトに置き換えるか</b></p>
               <p>抗原認識に必要なのは<b>CDR</b>だけ（→ med01）なので、
               それ以外をヒト配列に替えれば特異性を保ったまま免疫原性を下げられます。</p>
               <table>
                 <tr><th>世代</th><th>ヒト配列の割合</th><th>語尾</th><th>例</th></tr>
                 <tr><td>マウス</td><td>0%</td><td>-omab</td><td>ムロモナブ</td></tr>
                 <tr><td>キメラ（可変領域だけマウス）</td><td>約65%</td><td><b>-ximab</b></td><td>リツキシマブ</td></tr>
                 <tr><td>ヒト化（CDRだけマウス）</td><td>約95%</td><td><b>-zumab</b></td><td>トラスツズマブ</td></tr>
                 <tr><td>完全ヒト</td><td>100%</td><td><b>-umab</b></td><td>ニボルマブ</td></tr>
               </table>
               <p><b>【ステップ3】語尾は暗記でなく構造の情報</b></p>
               <p>一般名の語尾を見れば由来がわかります。
               完全ヒト抗体は、ヒト抗体遺伝子を導入したマウスや<b>ファージディスプレイ</b>で得られます。</p>
               <p class="tiny">ヒト化しても免疫原性がゼロになるわけではなく、
               抗薬物抗体（ADA）の測定は今も必須です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med06", ...CAT,
    title: "抗体医薬の4つの作用機序", diff: 225, tags: ["抗体医薬", "ADCC", "ADC", "中和"],
    explain: `<p>抗体医薬は「標的に結合する」だけでは薬になりません。次の4剤について、
              主たる作用機序を対応づけたい。</p>
              <ol>
                <li>インフリキシマブ（抗TNF-α）</li>
                <li>リツキシマブ（抗CD20）</li>
                <li>トラスツズマブ エムタンシン（抗HER2＋薬物）</li>
                <li>ニボルマブ（抗PD-1）</li>
              </ol>
              <p>正しい組み合わせはどれか。</p>`,
    type: "radio",
    options: [
      "1=中和、2=ADCC/CDC、3=薬物送達（ADC）、4=抑制の解除",
      "1=ADCC、2=中和、3=抑制の解除、4=薬物送達",
      "1=薬物送達、2=抑制の解除、3=中和、4=ADCC",
      "4剤すべてが標的の酵素活性を直接阻害する",
    ],
    answer: 0,
    hints: [
      "TNF-α は分泌される可溶性のサイトカインです。抗体が結合すると受容体に届きません。",
      "CD20 はB細胞表面の分子です。抗体の Fc に NK 細胞や補体が集まると何が起きるでしょう。",
    ],
    solution: `<p><b>【ステップ1】標的が「可溶性」か「細胞表面」かで分ける</b></p>
               <p>これが最初の分岐点です。</p>
               <ul>
                 <li><b>可溶性リガンド</b>が標的 → 受容体に届かないよう<b>中和</b>する
                     （インフリキシマブ、ベバシズマブ＝抗VEGF）</li>
                 <li><b>細胞表面分子</b>が標的 → その細胞をどうするかを設計できる</li>
               </ul>
               <p><b>【ステップ2】細胞表面標的の3つの使い道</b></p>
               <table>
                 <tr><th>機序</th><th>内容</th><th>例</th></tr>
                 <tr><td><b>ADCC / CDC</b></td><td>Fc に NK細胞・マクロファージ・補体を呼び、標的細胞を殺す</td><td>リツキシマブ</td></tr>
                 <tr><td><b>ADC</b></td><td>抗体を運び屋にし、内在化後に細胞内で薬物を放出</td><td>T-DM1</td></tr>
                 <tr><td><b>シグナル遮断</b></td><td>受容体の二量体化やリガンド結合を妨げる</td><td>トラスツズマブ（裸の抗体として）</td></tr>
               </table>
               <p><b>【ステップ3】免疫チェックポイント阻害薬は「殺さない」抗体</b></p>
               <p>ニボルマブの標的 PD-1 は T細胞のブレーキです。
               抗体は PD-1 と PD-L1 の結合を邪魔するだけで、
               実際にがんを攻撃するのは<b>解除されたT細胞</b>です。
               標的細胞＝作用対象とは限らない好例です。</p>
               <p class="tiny">ADCC を強めるために Fc の糖鎖（フコース除去）や
               アミノ酸を改変した抗体（糖鎖改変抗体）も実用化されています。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "med07", ...CAT,
    title: "イマチニブと BCR-ABL", diff: 165, tags: ["分子標的薬", "イマチニブ", "BCR-ABL", "フィラデルフィア染色体"],
    explain: `<p>慢性骨髄性白血病（CML）では、9番と22番染色体の相互転座 t(9;22) により
              <b>BCR-ABL</b> 融合遺伝子ができる。
              イマチニブはこの融合タンパク質を標的とする。</p>
              <p>イマチニブの作用と、この薬が「分子標的薬」と呼ばれる理由の説明として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "DNAをアルキル化して切断する。増殖の速い細胞に効くので分子標的薬と呼ばれる。",
      "ABLキナーゼのATP結合ポケットに入り、恒常的なリン酸化を止める。正常細胞にない分子を狙うため副作用が抑えられる。",
      "融合遺伝子のmRNAを分解する。塩基配列を狙うため分子標的薬と呼ばれる。",
      "BCR-ABLに対する抗体で、ADCCにより白血病細胞を殺す。",
    ],
    answer: 1,
    hints: [
      "イマチニブは低分子化合物です。細胞内のタンパク質を狙えます。",
      "BCR-ABL は「常にオンになったチロシンキナーゼ」です。オンを止めるにはどこに何をはめればよいでしょう。",
    ],
    solution: `<p><b>【ステップ1】BCR-ABL は何が異常なのか</b></p>
               <p>ABL は本来、制御を受けるチロシンキナーゼです。
               BCR と融合すると自己会合して<b>恒常的活性型</b>になり、
               増殖シグナル（RAS-MAPK、PI3K、STAT5）を出しつづけます。
               <b>ドライバー変異</b>そのものです。</p>
               <p><b>【ステップ2】ATP競合阻害という手</b></p>
               <p>キナーゼはATPのγリン酸を基質に渡します。
               イマチニブはABLの<b>ATP結合ポケット</b>に、
               しかも<b>不活性型のコンフォメーション</b>に選択的にはまり込み、
               ATPが入れないようにします。基質はリン酸化されず、シグナルは止まります。</p>
               <p><b>【ステップ3】「分子標的薬」の意味</b></p>
               <p>従来の細胞毒性抗がん剤は「よく増える細胞」を狙うため、
               骨髄・消化管・毛髪も傷めました。
               分子標的薬は<b>がん細胞に固有の分子的な変化</b>を狙うので、
               治療域を広くとれます。CML の5年生存率が劇的に改善したのはこの結果です。</p>
               <p class="tiny">ただし完全に選択的ではなく、イマチニブは KIT や PDGFR も阻害します
               （GIST に効くのはこのため）。副作用も「別の標的への作用」として説明できます。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med08", ...CAT,
    title: "EGFR-TKI 耐性が生じるしくみ", diff: 285, tags: ["分子標的薬", "EGFR", "耐性変異", "T790M", "クローン選択"],
    explain: `<p>EGFR活性化変異をもつ肺がん患者にゲフィチニブを投与すると、
              腫瘍は速やかに縮小したが、約1年後に再増大した。
              再生検で <b>T790M</b> 変異（ATP結合ポケット近傍のゲートキーパー残基の置換）が検出された。</p>
              <p>この現象の解釈として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "薬剤が細胞に T790M 変異を誘発した。よって投薬量を減らせば耐性は避けられる。",
      "もともと存在した T790M をもつ少数のクローンが薬剤存在下で選択され、優勢になった。T790M でも結合できる第3世代阻害薬が有効なことがある。",
      "腫瘍がEGFRの発現を完全に失った。よってEGFR以外の薬に切り替える必要が必ずある。",
      "T790M は EGFR を不活性化するので、腫瘍の再増大とは無関係である。",
    ],
    answer: 1,
    hints: [
      "腫瘍は遺伝的に均一ではありません（腫瘍内不均一性）。薬剤は何をしているでしょうか。",
      "T790M があっても効く薬を作るには、ATP競合ではない結合様式が要ります。",
    ],
    solution: `<p><b>【ステップ1】耐性は「進化」である</b></p>
               <p>腫瘍は多数のサブクローンの集団です。
               薬剤はそこに強い<b>選択圧</b>をかけます。
               感受性クローンが消え、あらかじめ存在した耐性クローンが空いた場所を埋める。
               細菌の抗生物質耐性とまったく同じ論理です
               （超高感度シーケンスで、投与前検体にごく低頻度の T790M が検出されます）。</p>
               <p><b>【ステップ2】T790M がなぜ効きを落とすか</b></p>
               <p>790番のトレオニンは ATP ポケットの入口を見張る<b>ゲートキーパー</b>残基です。
               かさ高いメチオニンに変わると、</p>
               <ul>
                 <li>阻害薬がはまる隙間が狭くなる</li>
                 <li>同時に ATP への親和性が上がる</li>
               </ul>
               <p>結果として、ATP と競合する第1世代薬は競り負けます。</p>
               <p><b>【ステップ3】第3世代薬の設計</b></p>
               <p>オシメルチニブは <b>C797 と共有結合する不可逆阻害薬</b>で、
               T790M があっても結合できます。しかも野生型EGFRより変異型に選択性が高く、
               皮膚・消化器の副作用を抑えられます。</p>
               <p>さらに耐性が出るときは C797S 変異や、
               <b>MET増幅・小細胞がんへの形質転換</b>といった
               「別ルートでのシグナル回復（バイパス経路）」が起こります。
               <b>耐性機構を同定して次の薬を選ぶ</b>のが分子標的治療の基本戦略です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med09", ...CAT,
    title: "コンパニオン診断はなぜ必要か", diff: 205, tags: ["トラスツズマブ", "HER2", "コンパニオン診断", "バイオマーカー"],
    explain: `<p>トラスツズマブ（抗HER2抗体）は、HER2過剰発現の乳がんには有効だが、
              HER2陰性の乳がんには使われない。投与前に IHC / FISH で HER2 の状態を調べる
              <b>コンパニオン診断</b>が必須である。</p>
              <p>この考え方について正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "分子標的薬は標的分子に依存して効くため、標的の有無を確認しないと無効な治療に副作用と費用だけを負わせることになる。",
      "コンパニオン診断は薬の副作用を予測するためのもので、有効性とは無関係である。",
      "標的が陰性でも用量を増やせば効くので、診断は省略できる。",
      "抗体医薬はすべての細胞に取り込まれるため、標的の発現量は関係ない。",
    ],
    answer: 0,
    hints: [
      "標的が存在しない細胞に、標的を狙う薬を投与したらどうなるでしょうか。",
      "「効く人を選ぶ」ことが治療成績と臨床試験の設計に何をもたらすか考えてみましょう。",
    ],
    solution: `<p><b>【ステップ1】標的がなければ薬理作用は起こらない</b></p>
               <p>HER2 が過剰発現していない細胞に抗HER2抗体を投与しても、
               結合する相手がないので効果は期待できません。
               一方で心毒性などの副作用リスクと薬剤費は生じます。
               <b>効かない人に投与しないこと</b>自体が医療上の利益です。</p>
               <p><b>【ステップ2】臨床試験の見え方も変わる</b></p>
               <p>もし HER2 の状態を問わずに全乳がん患者で試験したら、
               有効な集団の効果が無効な集団に薄められ、
               「効果なし」と判定されかねません。
               <b>バイオマーカーで層別化する</b>ことで、
               本来の効果を検出できるようになります。
               これが「がん種ではなく分子で治療を選ぶ」流れの出発点です。</p>
               <p><b>【ステップ3】判定の実務</b></p>
               <table>
                 <tr><th>方法</th><th>見るもの</th></tr>
                 <tr><td>IHC（免疫組織化学）</td><td>HER2<b>タンパク質</b>の量（0/1+/2+/3+）</td></tr>
                 <tr><td>FISH</td><td>HER2<b>遺伝子のコピー数</b>増幅（IHC 2+ の判定に）</td></tr>
               </table>
               <p>他の例：EGFR変異とEGFR-TKI、BRAF V600E とベムラフェニブ、
               <i>BRCA1/2</i> 変異と PARP 阻害薬、MSI-High と免疫チェックポイント阻害薬。</p>
               <p class="tiny">近年は ADC のように、標的発現が低い症例でも効く薬が出てきており
               （HER2低発現乳がんへのトラスツズマブ デルクステカン）、
               「陽性・陰性」の線引き自体が更新されつつあります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "med10", ...CAT,
    title: "低分子薬と抗体医薬を使い分ける", diff: 145, tags: ["抗体医薬", "低分子薬", "バイオ医薬品", "半減期"],
    explain: `<p>細胞<b>内側</b>にあるキナーゼの活性を止めたい。
              低分子阻害薬と抗体医薬のどちらが適切か、またその理由はどれか。</p>`,
    type: "radio",
    options: [
      "抗体医薬。特異性が高いので細胞内の標的にも最適である。",
      "低分子薬。抗体は分子量が大きく細胞膜を通過できないため、細胞内標的には届かない。",
      "どちらでもよい。抗体はエンドサイトーシスで自由に細胞質へ入れる。",
      "抗体医薬。経口投与できるので患者の負担が小さい。",
    ],
    answer: 1,
    hints: [
      "IgG の分子量は約15万、低分子薬は数百です。",
      "抗体医薬が注射でしか投与できない理由も同じ性質から説明できます。",
    ],
    solution: `<p><b>【ステップ1】サイズが用途を決める</b></p>
               <table>
                 <tr><th></th><th>低分子薬</th><th>抗体医薬</th></tr>
                 <tr><td>分子量</td><td>〜500</td><td>約150,000</td></tr>
                 <tr><td>標的の場所</td><td><b>細胞内も可</b></td><td><b>細胞外・細胞表面のみ</b></td></tr>
                 <tr><td>投与</td><td>経口が可能</td><td>注射（消化されるため経口不可）</td></tr>
                 <tr><td>半減期</td><td>数時間〜1日</td><td><b>2〜3週間</b>（FcRn によるリサイクル）</td></tr>
                 <tr><td>特異性</td><td>オフターゲットが出やすい</td><td>非常に高い</td></tr>
                 <tr><td>製造</td><td>化学合成（安価・均一）</td><td>細胞培養（高価・不均一）</td></tr>
               </table>
               <p><b>【ステップ2】だから標的の場所で決まる</b></p>
               <p>細胞内のキナーゼやシグナル分子 → <b>低分子</b>（イマチニブ、ゲフィチニブ）。<br>
               分泌サイトカインや細胞表面受容体 → <b>抗体</b>（インフリキシマブ、トラスツズマブ）。</p>
               <p>同じ HER2 経路でも、細胞外領域を狙うトラスツズマブは抗体、
               細胞内キナーゼドメインを狙うラパチニブは低分子です。
               <b>標的の局在が薬のかたちを決める</b>という関係がよく見えます。</p>
               <p><b>【ステップ3】半減期の意味</b></p>
               <p>抗体は Fc が <b>FcRn</b> に結合してリソソーム分解を免れ、細胞外へ戻されます。
               このリサイクルのおかげで半減期が数週間になり、
               「月1回の注射」といった投与設計が可能になります。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med11", ...CAT,
    title: "二重特異性抗体は何を可能にしたか", diff: 275, tags: ["二重特異性抗体", "BiTE", "T細胞", "抗体工学"],
    explain: `<p>ブリナツモマブは、片方のアームで腫瘍細胞の CD19 を、
              もう片方のアームでT細胞の CD3 を認識する<b>二重特異性抗体（BiTE）</b>である。</p>
              <p>この分子設計が実現していることはどれか。</p>`,
    type: "radio",
    options: [
      "2種類の腫瘍抗原を同時に中和し、シグナルを二重に遮断している。",
      "腫瘍細胞とT細胞を物理的に架橋し、抗原提示やMHC拘束を経ずにT細胞を活性化して腫瘍細胞を殺させている。",
      "CD19 に結合したあと細胞内に入り、CD3 を分解している。",
      "Fc を介した ADCC を強化しているだけで、T細胞の関与はない。",
    ],
    answer: 1,
    hints: [
      "T細胞が普段標的を認識するには、MHC に提示された抗原とTCRの組み合わせが必要です。",
      "抗体が2つの細胞を「つなぐ」と、その界面に何ができるでしょうか。",
    ],
    solution: `<p><b>【ステップ1】通常のT細胞の認識と、その弱点</b></p>
               <p>T細胞は MHC 上のペプチドを TCR で見ます。
               ところが腫瘍はしばしば<b>MHC classⅠの発現を落として</b>この監視から逃れます。</p>
               <p><b>【ステップ2】架橋という発想</b></p>
               <p>BiTE は「CD19に結合する抗体断片」と「CD3に結合する抗体断片」を
               リンカーでつないだ小型分子（Fc をもたない scFv 2連結）です。
               両方に同時に結合すると、</p>
               <ul>
                 <li>腫瘍細胞とT細胞が数nmの距離で密着し、<b>人工的な免疫シナプス</b>ができる</li>
                 <li>CD3 の架橋によりT細胞が<b>MHC非依存的に</b>活性化する</li>
                 <li>パーフォリン／グランザイムが放出され、腫瘍細胞が殺される</li>
               </ul>
               <p>抗体を「殺し屋を連れてくる道具」に使い換えたのがこの設計です。</p>
               <p><b>【ステップ3】長所と代償</b></p>
               <p>Fc がないぶん分子量が小さく組織に入りやすい一方、
               半減期が数時間しかないため<b>持続点滴</b>が必要でした。
               またT細胞を強く活性化するので
               <b>サイトカイン放出症候群（CRS）</b>や神経毒性が起こり得ます。
               近年は Fc をもつ二重特異性抗体で半減期を延ばした薬が主流です。</p>
               <p class="tiny">CAR-T 細胞療法も「MHC非依存的にT細胞を標的へ向ける」点は同じ発想で、
               患者のT細胞を体外で改変するか、抗体分子で橋渡しするかの違いです。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "med12", ...CAT,
    title: "IC50 から選択性を評価する", diff: 245, tags: ["IC50", "選択性", "薬理", "計算"],
    explain: `<p>ある阻害薬について、標的キナーゼ X に対する IC<sub>50</sub> は <b>4 nM</b>、
              類縁キナーゼ Y に対する IC<sub>50</sub> は <b>1.2 µM</b> であった。</p>
              <p>Y に対する X の<b>選択性（何倍か）</b>を整数で答えなさい。</p>`,
    type: "text",
    answer: ["300", "300倍", "300 倍"],
    hints: [
      "1 µM = 1000 nM です。まず単位をそろえましょう。",
      "選択性は「効きにくい方の IC50 ÷ 効きやすい方の IC50」で表します。",
    ],
    solution: `<p><b>【ステップ1】単位をそろえる</b></p>
               <p>1.2 µM = 1.2 × 1000 nM = <b>1200 nM</b></p>
               <p><b>【ステップ2】比を取る</b></p>
               <p>1200 nM ÷ 4 nM = <b>300 倍</b></p>
               <p>X を止めるのに必要な濃度の 300 倍を入れないと Y は止まらない、
               つまり X に対して<b>300倍選択的</b>です。</p>
               <p><b>【ステップ3】この数字の読み方</b></p>
               <p>IC<sub>50</sub> は<b>小さいほど強い</b>阻害を意味します（50%阻害に要する濃度）。
               創薬では次のように使い分けます。</p>
               <ul>
                 <li><b>効力（potency）</b>：標的の IC<sub>50</sub> が十分小さいか</li>
                 <li><b>選択性（selectivity）</b>：他分子との IC<sub>50</sub> の比が十分大きいか</li>
               </ul>
               <p>血中で到達できる濃度が 50 nM 程度なら、X はほぼ完全に阻害され、
               Y はほとんど阻害されません。この差が<b>副作用の少なさ</b>につながります。
               逆に選択性が10倍程度しかない薬は、
               有効濃度でオフターゲットも叩いてしまいます。</p>
               <p class="tiny">なお IC<sub>50</sub> は測定条件（ATP濃度など）に依存する見かけの値です。
               ATP競合阻害薬では、ATP濃度が高いほど IC<sub>50</sub> は大きく出ます。
               条件を揃えない比較には意味がありません。</p>
               <p><b>【答え】300</b>（倍）</p>`,
  },
];
